import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useEffect, useRef, useState } from 'react';
import SextforceColorSelector from '../../../common/SextforceColorSelector';
import StyledCard from '../../../common/StyledCard';

type Props = {
    active: boolean;
    setActive: (active: boolean) => void;
    setQuery: (query: string) => void;
    sortBy: '_id' | 'name' | 'description' | 'nextRepostDate';
    setSortBy: (sortBy: '_id' | 'name' | 'description' | 'nextRepostDate') => void;
    colorId: string | undefined;
    setColorId: (colorId: string | undefined) => void;
    theme: Theme;
};

const SextforceAutoRepostSearchBar = (props: Props) => {
    const { active, setActive, setQuery, sortBy, setSortBy, colorId, setColorId, theme } = props;

    const [queryText, setQueryText] = useState<string>('');
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Clean up timeout on component unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setQueryText(newValue);

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set a new timeout
        timeoutRef.current = setTimeout(() => {
            setQuery(newValue);
        }, 500);
    };

    return (
        <StyledCard noCard noBottomMargin>
            <Box
                sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    pt: 2,
                    // Elevation 1
                    boxShadow: theme.shadows[1],
                    borderRadius: theme.shape.borderRadius,
                }}
            >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs="auto">
                                    <ToggleButtonGroup
                                        color="primary"
                                        value={active}
                                        exclusive
                                        size="small"
                                        onChange={(event, value) => {
                                            if (value !== null) {
                                                setActive(value);
                                            }
                                        }}
                                        sx={{
                                            mt: '4px',
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    >
                                        <ToggleButton value={true}>Active</ToggleButton>
                                        <ToggleButton value={false}>Inactive</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>

                                <Grid item xs>
                                    <TextField
                                        fullWidth
                                        label="Search"
                                        value={queryText}
                                        onChange={handleQueryChange}
                                        onKeyDown={e => {
                                            // Clear on Escape
                                            if (e.key === 'Escape') {
                                                if (timeoutRef.current) {
                                                    clearTimeout(timeoutRef.current);
                                                }
                                                setQuery('');
                                                setQueryText('');
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: queryText ? (
                                                <ClearOutlinedIcon
                                                    onClick={() => {
                                                        if (timeoutRef.current) {
                                                            clearTimeout(timeoutRef.current);
                                                        }
                                                        setQuery('');
                                                        setQueryText('');
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            ) : undefined,
                                        }}
                                        sx={{
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs sm>
                            <SextforceColorSelector
                                colorId={colorId}
                                setColorId={setColorId}
                                allowEdit={false}
                                size="small"
                                noneName="Any"
                            />
                        </Grid>

                        <Grid item xs sm>
                            <FormControl
                                fullWidth
                                size="small"
                                sx={{
                                    mt: 0,
                                    mb: 0,
                                }}
                            >
                                <InputLabel id="sort-by-label">Sort By</InputLabel>
                                <Select
                                    labelId="sort-by-label"
                                    label="Sort By"
                                    fullWidth
                                    size="small"
                                    value={sortBy}
                                    onChange={e => {
                                        setSortBy(e.target.value as '_id' | 'name' | 'description' | 'nextRepostDate');
                                    }}
                                    sx={{
                                        backgroundColor: theme.palette.background.paper,
                                    }}
                                >
                                    <MenuItem value={'_id'}>Date Added</MenuItem>
                                    <MenuItem value={'name'}>Name</MenuItem>
                                    <MenuItem value={'description'}>Description</MenuItem>
                                    <MenuItem value={'nextRepostDate'}>Next Repost Date</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </Box>
        </StyledCard>
    );
};

export default SextforceAutoRepostSearchBar;
